import React, { useState, useRef, useLayoutEffect } from 'react';
import {
  SERP_TITLE_MAX_CHARS,
  SERP_DESC_MAX_CHARS,
  truncateSerpText,
  serpBreadcrumb,
} from '../utils/googleSerpPreview';

const STATUS_BADGE = {
  ok: 'text-green-700 bg-green-50 border-green-200',
  warning: 'text-amber-800 bg-amber-50 border-amber-200',
  error: 'text-red-700 bg-red-50 border-red-200',
};

/**
 * Aperçu SERP calqué sur le rendu Google desktop actuel :
 * favicon + fil d’Ariane, titre bleu, extrait gris (troncature ~60 / 160 car.).
 */
export default function GoogleSerpPreview({
  title,
  url,
  metaDescription,
  titleStatus,
  titleLen,
  className = '',
  /** Réduit visuellement tout en gardant les proportions SERP (600px). */
  scale = 0.88,
  showTitleBadge = true,
}) {
  const { site, path, favicon } = serpBreadcrumb(url);
  const hasTitle = Boolean((title || '').trim());
  const hasMeta = Boolean((metaDescription || '').trim());

  const displayTitle = hasTitle
    ? truncateSerpText(title, SERP_TITLE_MAX_CHARS)
    : 'Titre manquant (balise title)';
  const displayMeta = hasMeta
    ? truncateSerpText(metaDescription, SERP_DESC_MAX_CHARS)
    : 'Meta description absente — Google choisira un extrait au hasard.';

  const titleTruncated = hasTitle && title.trim().length > SERP_TITLE_MAX_CHARS;
  const metaTruncated = hasMeta && metaDescription.trim().length > SERP_DESC_MAX_CHARS;

  const [faviconFailed, setFaviconFailed] = useState(false);
  const serpInnerRef = useRef(null);
  const [scaledHeight, setScaledHeight] = useState(null);

  useLayoutEffect(() => {
    const el = serpInnerRef.current;
    if (!el || scale === 1) {
      setScaledHeight(null);
      return undefined;
    }
    const update = () => setScaledHeight(Math.ceil(el.offsetHeight * scale));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale, title, metaDescription, titleTruncated, metaTruncated, hasTitle, hasMeta]);

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400">
          Aperçu dans Google
        </p>
        <p className="text-[10px] text-blue-gray-400">Desktop · rendu estimé</p>
      </div>

      <div
        className="overflow-hidden max-w-full"
        style={scaledHeight != null ? { height: scaledHeight } : undefined}
      >
        <div
          ref={serpInnerRef}
          className="inline-block origin-top-left"
          style={
            scale !== 1
              ? { transform: `scale(${scale})`, width: 600, maxWidth: '100%' }
              : { width: 600, maxWidth: '100%' }
          }
        >
        <div
          className="rounded-lg border border-[#dadce0] bg-white px-4 py-3"
          style={{ width: 600, maxWidth: '100%', fontFamily: 'Arial, sans-serif' }}
        >
          {/* Ligne site (favicon + domaine + chemin) — comme sur les SERP Google récents */}
          <div className="flex items-center gap-2.5 min-w-0 mb-1">
            {favicon && !faviconFailed ? (
              <img
                src={favicon}
                alt=""
                width={26}
                height={26}
                className="w-[26px] h-[26px] rounded-full shrink-0 bg-[#f1f3f4]"
                loading="lazy"
                decoding="async"
                onError={() => setFaviconFailed(true)}
              />
            ) : (
              <span
                className="w-[26px] h-[26px] rounded-full shrink-0 bg-[#f1f3f4] border border-[#dadce0]"
                aria-hidden
              />
            )}
            <cite className="not-italic text-sm leading-tight min-w-0 truncate">
              <span className="text-[#202124]">{site || 'votre-site.com'}</span>
              {path && (
                <span className="text-[#5f6368]">
                  {' '}
                  ›
                  {path}
                </span>
              )}
            </cite>
          </div>

          {/* Titre cliquable */}
          <h3
            className={`m-0 p-0 text-[20px] leading-[1.3] font-normal cursor-default ${
              hasTitle ? 'text-[#1a0dab] hover:underline' : 'text-[#70757a] italic text-base'
            }`}
            title={hasTitle ? title : undefined}
          >
            {displayTitle}
          </h3>

          {/* Extrait */}
          <p
            className={`m-0 mt-1 text-sm leading-[1.58] ${
              hasMeta ? 'text-[#4d5156]' : 'text-[#70757a] italic'
            }`}
            style={{ WebkitLineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            title={hasMeta ? metaDescription : undefined}
          >
            {displayMeta}
          </p>

          {(titleTruncated || metaTruncated) && (
            <p className="mt-2 text-[11px] text-[#70757a] leading-snug">
              {titleTruncated && `Titre tronqué au-delà de ~${SERP_TITLE_MAX_CHARS} car. `}
              {metaTruncated && `Description tronquée au-delà de ~${SERP_DESC_MAX_CHARS} car.`}
            </p>
          )}
        </div>
        </div>
      </div>

      {showTitleBadge && titleStatus && titleLen != null && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border tabular-nums ${
              STATUS_BADGE[titleStatus.tone] || STATUS_BADGE.warning
            }`}
          >
            Title · {titleLen} car. · {titleStatus.label}
          </span>
        </div>
      )}
    </div>
  );
}
